import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VendorByEmailDialog from "@/components/VendorByEmailDialog";
import { ChevronLeft, CheckCircle2, Clock, Lock, AlertTriangle, ShieldAlert, Pencil, MessageSquare, FileText } from "lucide-react";
import { FALLBACK_APPROVER_ROLES, ADMIN_ROLE } from "@/constants/roles";
import { uomLabel } from "@/constants/uom";
import DocumentsPanel from "@/components/DocumentsPanel";
import ChatPanel from "@/components/chat/ChatPanel";
import ConfirmAction from "@/components/ConfirmAction";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PurchaseRequestDetail() {
  const { id } = useParams();
  const [pr, setPr] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showPO, setShowPO] = useState(false);
  const [showRFQ, setShowRFQ] = useState(false);
  const [poVendor, setPoVendor] = useState("");
  const [showByEmail, setShowByEmail] = useState(false);
  const [poDelivery, setPoDelivery] = useState("");
  const [poPaymentMethod, setPoPaymentMethod] = useState("card");
  const [rfqTitle, setRfqTitle] = useState("");
  const [rfqVendors, setRfqVendors] = useState([]);
  const [rfqDue, setRfqDue] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showModify, setShowModify] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [showModifyApprove, setShowModifyApprove] = useState(false);
  const [modItems, setModItems] = useState([]);
  const [modComment, setModComment] = useState("");
  const [question, setQuestion] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [sourceRfq, setSourceRfq] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => api.get(`/contractor/purchase-requests/${id}`).then((r) => {
    // Package 44 — once approved this IS a PO now: open it.
    if (r.data.status === "converted" && r.data.po_id) {
      navigate(`/contractor/purchase-orders/${r.data.po_id}`, { replace: true });
      return;
    }
    setPr(r.data);
    if (r.data.source_rfq_id) {
      api.get(`/contractor/rfqs/${r.data.source_rfq_id}`).then((rr) => setSourceRfq(rr.data)).catch(() => setSourceRfq(null));
    }
  });

  useEffect(() => {
    load();
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data));
    api.get("/contractor/cost-codes", { params: { include_inactive: true } }).then((r) => setCostCodes(r.data));
    api.get("/contractor/delivery-addresses", { params: { include_inactive: true } }).then((r) => setAddresses(r.data));
  }, [id]);

  if (!pr) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const costCodeLabel = (id) => {
    const cc = costCodes.find((c) => c.id === id);
    return cc ? `${cc.code} — ${cc.name}` : "—";
  };
  const address = addresses.find((a) => a.id === pr.delivery_address_id);

  // Strict ordering: only the chain's first still-pending step is ever
  // actionable — never a later one, even if its role matches the viewer.
  const activeStepIndex = pr.approval_chain.findIndex((s) => s.status === "pending");
  const activeStep = activeStepIndex >= 0 ? pr.approval_chain[activeStepIndex] : null;
  const isFallbackReviewer = FALLBACK_APPROVER_ROLES.includes(user?.role);
  const myPendingStep = activeStep && (activeStep.approver_role === user?.role || isFallbackReviewer) ? activeStep : null;
  const isAdmin = user?.role === ADMIN_ROLE;
  const isRequester = user?.id === pr.requested_by;
  const canEdit = pr.status === "draft" && (isRequester || isAdmin);
  const canOverride = isAdmin && pr.status === "awaiting_approval";

  const act = async (action, comment) => {
    try {
      await api.post(`/contractor/purchase-requests/${id}/${action}`, { comment });
      toast.success("Updated");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Action failed");
    }
  };

  const submitModify = () => {
    if (!decisionComment.trim()) return toast.error("A comment describing what needs to change is required");
    act("request-modification", decisionComment);
    setShowModify(false);
  };

  const openModifyApprove = () => {
    setModItems((pr.items || []).map((i) => ({ ...i })));
    setModComment("");
    setShowModifyApprove(true);
  };

  const submitModifyApprove = async () => {
    try {
      const r = await api.post(`/contractor/purchase-requests/${id}/modify-approve`, {
        items: modItems.map((i) => ({
          description: i.description, qty: Number(i.qty), uom: i.uom || "each",
          unit_price: i.unit_price === "" || i.unit_price === null ? null : Number(i.unit_price),
          cost_code_id: i.cost_code_id || null, billability: i.billability,
          markup_percent: i.markup_percent,
        })),
        comment: modComment || null,
      });
      toast.success(`Approved with changes: ${r.data.modification_summary}`);
      setShowModifyApprove(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Modify & approve failed");
    }
  };

  const postQuestion = async () => {
    if (!question.trim()) return;
    try {
      await api.post(`/contractor/purchase-requests/${id}/questions`, { message: question.trim() });
      setQuestion("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    }
  };

  const submitOverride = async () => {
    if (overrideReason.trim().length < 10) return toast.error("A reason of at least 10 characters is required");
    try {
      await api.post(`/contractor/purchase-requests/${id}/override-approve`, { reason: overrideReason });
      toast.success("Approval overridden");
      setShowOverride(false);
      setOverrideReason("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Override failed");
    }
  };

  const submitPR = async () => {
    try {
      await api.post(`/contractor/purchase-requests/${id}/submit`);
      toast.success("Submitted for approval");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Failed to submit");
    }
  };

  const isCheckout = pr?.source === "catalog"; // Package 22: catalog-origin PRs go through real checkout, not a plain conversion
  const selectedVendorEntry = vendors.find((v) => v.vendor_id === poVendor);

  const convertToPO = async () => {
    if (!poVendor) return toast.error("Select a vendor");
    try {
      const { data } = await api.post(`/contractor/purchase-requests/${id}/convert-to-po`, {
        vendor_id: poVendor, expected_delivery: poDelivery || null,
        payment_method: poPaymentMethod === "ramp" ? "ramp" : (isCheckout ? poPaymentMethod : undefined),
      });
      toast.success(isCheckout ? "Purchase order created — proceed to send it to the vendor" : "Purchase order created");
      navigate(`/contractor/purchase-orders/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Failed");
    }
  };

  const convertToRFQ = async () => {
    if (!rfqTitle || rfqVendors.length === 0) return toast.error("Add a title and select vendors");
    try {
      const { data } = await api.post(`/contractor/purchase-requests/${id}/convert-to-rfq`, { title: rfqTitle, vendor_ids: rfqVendors, due_date: rfqDue || null });
      toast.success("RFQ created");
      navigate(`/contractor/rfqs/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="pr-detail-page">
      <Link to="/contractor/purchase-orders" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Orders</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight"><span className="text-slate-400 font-semibold text-[15px] mr-2">Purchase Order</span><span className="mono">{pr.pr_number}</span></h1>
            <StatusBadge status={pr.status} />
            {pr.has_unpriced_lines && <span className="ss-pill ss-pill-amber" data-testid="unpriced-lines-badge">Contains unpriced lines</span>}
          </div>
          <p className="text-slate-500 text-sm mt-1">{pr.context_label || "—"} · Requested by {pr.requested_by_name}</p>
          {sourceRfq && (
            <Link to={`/contractor/rfqs/${sourceRfq.id}`} className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-sky-600 font-semibold" data-testid="pr-source-rfq-link">
              <FileText size={12} /> Awarded from {sourceRfq.rfq_number}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button onClick={() => navigate(`/contractor/purchase-requests/${id}/edit`)} data-testid="edit-pr-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
              <Pencil size={14} /> Edit
            </button>
          )}
          {pr.status === "draft" && <button onClick={submitPR} data-testid="submit-pr-button" className="px-4 py-2.5 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Submit for Approval</button>}
          {myPendingStep && (
            <>
              <button onClick={() => act("approve")} data-testid="approve-pr-button" className="px-4 py-2.5 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold">Approve</button>
              <button onClick={openModifyApprove} data-testid="modify-approve-button" className="px-4 py-2.5 rounded-lg border border-emerald-300 text-emerald-700 text-[13px] font-semibold hover:bg-emerald-50">Modify & Approve</button>
              <button onClick={() => { setDecisionComment(""); setShowModify(true); }} data-testid="request-modification-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold">Request Changes</button>
              <button onClick={() => { setDecisionComment(""); setShowReject(true); }} data-testid="reject-pr-button" className="px-4 py-2.5 rounded-lg border border-red-200 text-red-600 text-[13px] font-semibold">Reject</button>
            </>
          )}
          {canOverride && (
            <button onClick={() => { setOverrideReason(""); setShowOverride(true); }} data-testid="override-pr-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-[13px] font-semibold hover:bg-amber-50">
              <ShieldAlert size={14} /> Override
            </button>
          )}
          {(pr.requested_by === user?.id || user?.role === ADMIN_ROLE) && !["converted", "cancelled"].includes(pr.status) && (
            <button onClick={() => setConfirmDelete(true)} data-testid="delete-pr-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-red-600 text-[13px] font-semibold hover:bg-red-50">Delete</button>
          )}
          {pr.status === "approved" && pr.source !== "punchout" && (
            <>
              <button onClick={() => { setPoVendor(pr.vendor_id || ""); setPoPaymentMethod("card"); setShowPO(true); }} data-testid="convert-to-po-button" className="px-4 py-2.5 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">
                {pr.source === "catalog" ? "Choose payment & finish" : "Choose vendor & create PO"}
              </button>
              <button onClick={() => setShowRFQ(true)} data-testid="convert-to-rfq-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold">Send to RFQ</button>
            </>
          )}
        </div>
      </div>

      {pr.source === "punchout" && (
        <div className="mt-4 text-[13px] text-sky-900 bg-sky-50 border border-sky-100 rounded-lg px-3.5 py-3" data-testid="punchout-pr-banner">
          <div className="font-semibold text-[11px] uppercase tracking-wide text-sky-600 mb-0.5">Punchout Order</div>
          {pr.status === "draft" && "This cart came back from the supplier's punchout site — assign cost codes to every line, then submit for approval."}
          {pr.status === "awaiting_approval" && "Punchout cart awaiting approval. Once approved, the order transmits to the supplier electronically as a cXML OrderRequest."}
          {pr.status === "approved" && (
            <span>
              Approved — <strong>transmit the order to the supplier electronically</strong> (cXML OrderRequest).
              A real purchase order is created here the moment they accept it; their order confirmation attaches automatically.
              {pr.punchout_session_id && (
                <span className="flex gap-2 flex-wrap mt-2">
                  <button
                    onClick={async () => {
                      try {
                        const r = await api.post(`/contractor/punchout/sessions/${pr.punchout_session_id}/transmit-order`);
                        toast.success(`${r.data.po_number} transmitted — supplier said ${r.data.transmission.code} ${r.data.transmission.text}`);
                        navigate(`/contractor/purchase-orders/${r.data.po_id}`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Transmission failed");
                      }
                    }}
                    data-testid="transmit-order-button"
                    className="px-3.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12.5px] font-semibold"
                  >
                    Transmit Order to Supplier
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/contractor/punchout/sessions/${pr.punchout_session_id}/mark-ordered`);
                        toast.success("Marked as ordered at the supplier");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to mark ordered");
                      }
                    }}
                    data-testid="mark-ordered-button"
                    className="px-3.5 py-2 rounded-lg border border-sky-200 text-sky-700 text-[12.5px] font-semibold hover:bg-sky-100"
                  >
                    Ordered manually at supplier
                  </button>
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {pr.last_decision_comment && (
        <div className="mt-4 flex items-start gap-2 text-[13px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3" data-testid="pr-last-decision-comment">
          <MessageSquare size={15} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-[11px] uppercase tracking-wide text-amber-600 mb-0.5">
              {pr.status === "rejected" ? "Rejection Reason" : "Changes Requested"}
            </div>
            {pr.last_decision_comment}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-6 text-[13px]">
        <div className="border border-slate-200 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Delivery Address</div>
          {address ? (
            <div>
              <div className="font-medium">{address.label}</div>
              <div className="text-slate-500">{address.address_line1}{address.city ? `, ${address.city}, ${address.state} ${address.zip}` : ""}</div>
            </div>
          ) : <div className="text-slate-400">Not set</div>}
        </div>
        <div className="border border-slate-200 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Expected Delivery</div>
          <div>{pr.expected_delivery_date ? fmtDate(pr.expected_delivery_date) : "Not set"}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="pr-items-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">Description</th>
              <th className="text-left py-2">Cost Code</th>
              <th className="text-right py-2">Qty</th>
              <th className="text-right py-2">Unit Price</th>
              <th className="text-center py-2">Billability</th>
              <th className="text-right py-2">Line Total</th>
            </tr>
          </thead>
          <tbody>
            {pr.items.map((it, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="py-2.5">{it.description}</td>
                <td className="py-2.5 text-slate-500">{costCodeLabel(it.cost_code_id)}</td>
                <td className="py-2.5 text-right">{it.qty} {uomLabel(it.uom || it.unit)}</td>
                <td className="py-2.5 text-right mono">{fmt(it.unit_price)}</td>
                <td className="py-2.5 text-center capitalize">{(it.billability || "").replace("_", " ")}</td>
                <td className="py-2.5 text-right mono font-medium">{fmt(it.extended_price ?? it.qty * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* No tax at ordering time (the vendor adds it on their invoice) —
                the Tax row only shows on older PRs created when ordering did. */}
            {pr.tax_total > 0 && <tr><td colSpan={5} className="text-right py-1.5 text-slate-500">Subtotal</td><td className="text-right py-1.5 mono">{fmt(pr.subtotal)}</td></tr>}
            {pr.tax_total > 0 && <tr><td colSpan={5} className="text-right py-1.5 text-slate-500">Tax</td><td className="text-right py-1.5 mono">{fmt(pr.tax_total)}</td></tr>}
            <tr><td colSpan={5} className="text-right py-2 font-bold">Total{!(pr.tax_total > 0) && <span className="font-normal text-[11px] text-slate-400"> (before tax)</span>}</td><td className="text-right py-2 font-bold mono">{fmt(pr.amount)}</td></tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-8">
        <div className="text-[14.5px] font-bold mb-3">Approval Chain</div>
        <div className="space-y-2" data-testid="approval-chain">
          {pr.approval_chain.map((step, i) => {
            const isActive = i === activeStepIndex;
            const isFuture = step.status === "pending" && !isActive;
            return (
              <div
                key={i}
                className={`flex items-start gap-3 text-[13px] rounded-lg px-3 py-2.5 ${isActive ? "bg-amber-50 border border-amber-200" : "border border-transparent"} ${isFuture ? "opacity-50" : ""}`}
                data-testid={`chain-step-${i}`}
              >
                {step.status === "approved" && <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />}
                {step.status === "overridden" && <ShieldAlert size={16} className="text-purple-500 mt-0.5 shrink-0" />}
                {step.status === "pending" && <Clock size={16} className={`mt-0.5 shrink-0 ${isActive ? "text-amber-500" : "text-slate-300"}`} />}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="capitalize font-medium">{step.approver_role.replace(/_/g, " ")}</span>
                    {step.status === "approved" && <StatusBadge status="approved" />}
                    {step.status === "overridden" && <StatusBadge status="overridden" />}
                    {isActive && <StatusBadge status="awaiting_approval" />}
                    {isFuture && <span className="ss-pill ss-pill-gray" data-testid={`chain-step-waiting-${i}`}>Waiting</span>}
                    {step.escalated && (
                      <span className="flex items-center gap-1 ss-pill ss-pill-red" data-testid={`chain-step-escalated-${i}`}>
                        <AlertTriangle size={11} /> Escalated
                      </span>
                    )}
                  </div>
                  {step.comment && <div className="text-slate-500 mt-1">{step.comment}</div>}
                </div>
              </div>
            );
          })}
          {pr.approval_chain.length === 0 && <div className="text-slate-400 text-[13px]">Not yet submitted.</div>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        {pr.vendor_notes && (
          <div className="text-[13px] text-slate-600 bg-slate-50 rounded-lg p-3" data-testid="pr-vendor-notes">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Vendor Notes</div>
            {pr.vendor_notes}
          </div>
        )}
        {pr.internal_notes && (
          <div className="text-[13px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="pr-internal-notes">
            <div className="text-[11px] uppercase tracking-wide text-amber-600 font-semibold mb-1 flex items-center gap-1"><Lock size={11} /> Internal Only — Never Shown to Vendor</div>
            {pr.internal_notes}
          </div>
        )}
      </div>

      <DocumentsPanel entityType="purchase_request" entityId={pr.id} />

      <ConfirmAction open={confirmDelete} onOpenChange={setConfirmDelete} title={`Delete ${pr.pr_number}?`}
        description="This purchase order hasn't been sent, so it's removed completely. The audit trail keeps a record."
        confirmLabel="Delete" testId="delete-pr-dialog"
        onConfirm={async () => { await api.delete(`/contractor/purchase-requests/${pr.id}`); toast.success(`${pr.pr_number} deleted`); navigate("/contractor/purchase-orders"); }} />

      {/* Meeting 3 — talk to the vendor while the PO is still being put
          together, not only after it's sent. */}
      {pr.vendor_id && (
        <div className="mt-8" data-testid="pr-vendor-chat">
          <ChatPanel contractorTenantId={user?.tenant_id} vendorId={pr.vendor_id} subjectType="po_draft" subjectId={pr.id} subjectLabel={`PO draft ${pr.pr_number}`} />
        </div>
      )}

      <Dialog open={showPO} onOpenChange={setShowPO}>
        <DialogContent data-testid="convert-po-modal">
          <DialogHeader><DialogTitle>{isCheckout ? "Checkout" : "Convert to Purchase Order"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[13px] font-medium">Vendor *</label>
              <SearchableSelect
                value={poVendor}
                onChange={(e) => { if (e.target.value === "__email__") return setShowByEmail(true); setPoVendor(e.target.value); setPoPaymentMethod("card"); }}
                data-testid="po-vendor-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">Select vendor…</option>
                <option value="__email__">+ Someone not on ScapeSource — send by email…</option>
                {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
              </SearchableSelect>
              <VendorByEmailDialog open={showByEmail} onOpenChange={setShowByEmail} onCreated={async (v) => {
                const r = await api.get("/contractor/vendor-network"); setVendors(r.data); setPoVendor(v.vendor_id); setPoPaymentMethod("card");
              }} />
            </div>
            <div>
              <label className="text-[13px] font-medium">Expected Delivery</label>
              <input type="date" value={poDelivery} onChange={(e) => setPoDelivery(e.target.value)} data-testid="po-delivery-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            {isCheckout && poVendor && (
              <div>
                {/* "Pay by card" previously read as if ScapeSource itself charges a
                    card here — it doesn't; this only tags how the order was/will be
                    paid so the QuickBooks sync books it correctly (Purchase/Expense
                    vs Bill, see qbo.py's _is_ap_path). Relabeled to make that clear;
                    the underlying payment_method: "card" value is unchanged, since
                    it's still the only checkout path for vendors with no on-account
                    credit (see the disabled-button case below). */}
                <label className="text-[13px] font-medium">Order type</label>
                <div className="flex gap-2 mt-1.5">
                  <button
                    type="button" onClick={() => setPoPaymentMethod("card")} data-testid="pay-by-card-button"
                    className={`flex-1 h-10 rounded-lg text-[12.5px] font-semibold ${poPaymentMethod === "card" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
                  >
                    Card (paid directly with vendor)
                  </button>
                  <button
                    type="button" disabled={!selectedVendorEntry?.on_account_allowed}
                    onClick={() => setPoPaymentMethod("on_account")} data-testid="pay-on-account-button"
                    title={selectedVendorEntry?.on_account_allowed ? "" : "This vendor doesn't offer on-account credit for your company"}
                    className={`flex-1 h-10 rounded-lg text-[12.5px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${poPaymentMethod === "on_account" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
                  >
                    On account{selectedVendorEntry?.on_account_allowed ? ` (${selectedVendorEntry.terms?.replace("_", " ")})` : ""}
                  </button>
                  <button
                    type="button" onClick={() => setPoPaymentMethod("ramp")} data-testid="pay-by-ramp-button"
                    className={`flex-1 h-10 rounded-lg text-[12.5px] font-semibold ${poPaymentMethod === "ramp" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
                  >
                    Ramp
                  </button>
                </div>
                {!selectedVendorEntry?.on_account_allowed && (
                  <p className="text-[11.5px] text-slate-400 mt-1.5">This vendor doesn't offer on-account credit for your company — card only.</p>
                )}
              </div>
            )}
            {!isCheckout && poVendor && (
              <label className="flex items-start gap-2 text-[12.5px] text-slate-700">
                <input type="checkbox" checked={poPaymentMethod === "ramp"} onChange={(e) => setPoPaymentMethod(e.target.checked ? "ramp" : "card")} className="mt-0.5" data-testid="pay-with-ramp-checkbox" />
                <span>Pay this order with Ramp</span>
              </label>
            )}
            <button onClick={convertToPO} data-testid="confirm-convert-po-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-2">
              {isCheckout ? "Submit Order" : "Create Purchase Order"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRFQ} onOpenChange={setShowRFQ}>
        <DialogContent data-testid="convert-rfq-modal">
          <DialogHeader><DialogTitle>Send to RFQ</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[13px] font-medium">RFQ Title *</label>
              <input value={rfqTitle} onChange={(e) => setRfqTitle(e.target.value)} data-testid="rfq-title-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[13px] font-medium">Vendors *</label>
              <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto ss-scrollbar">
                {vendors.map((v) => (
                  <label key={v.vendor_id} className="flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={rfqVendors.includes(v.vendor_id)} data-testid={`rfq-vendor-checkbox-${v.vendor_id}`} onChange={(e) => setRfqVendors(e.target.checked ? [...rfqVendors, v.vendor_id] : rfqVendors.filter((x) => x !== v.vendor_id))} />
                    {v.vendor_name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[13px] font-medium">Due Date</label>
              <input type="date" value={rfqDue} onChange={(e) => setRfqDue(e.target.value)} data-testid="rfq-due-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={convertToRFQ} data-testid="confirm-convert-rfq-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-2">Create RFQ</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent data-testid="reject-modal">
          <DialogHeader><DialogTitle>Reject Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} placeholder="Reason for rejection" rows={3} data-testid="reject-comment-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={() => { act("reject", decisionComment); setShowReject(false); }} data-testid="confirm-reject-button" className="w-full h-10 rounded-lg bg-red-500 text-white text-[13px] font-semibold">Reject</button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5 max-w-3xl" data-testid="pr-questions-panel">
        <h2 className="text-[15px] font-bold">Questions</h2>
        <p className="text-[11.5px] text-slate-400 mt-0.5">Approver ↔ requester thread — asking never blocks or changes the approval.</p>
        <div className="mt-3 space-y-2.5">
          {(pr.questions || []).map((q) => (
            <div key={q.id} className="text-[13px]" data-testid={`question-${q.id}`}>
              <span className="font-semibold">{q.author_name}</span>
              <span className="text-slate-400 text-[11.5px] ml-2">{fmtDateTime(q.created_at)}</span>
              <div className="text-slate-600 mt-0.5">{q.message}</div>
            </div>
          ))}
          {(pr.questions || []).length === 0 && <div className="text-[12.5px] text-slate-400">No questions yet.</div>}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postQuestion()} placeholder="Ask or answer…" data-testid="question-input" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
          <button onClick={postQuestion} data-testid="post-question-button" className="px-4 h-10 rounded-lg bg-slate-900 text-white text-[12.5px] font-semibold">Post</button>
        </div>
      </div>

      <Dialog open={showModify} onOpenChange={setShowModify}>
        <DialogContent data-testid="modify-modal">
          <DialogHeader><DialogTitle>Request Changes</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">The request will go back to the requester as an editable draft — a comment is required so they know what to fix.</p>
            <textarea value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} placeholder="What needs to change? *" rows={3} data-testid="modify-comment-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitModify} data-testid="confirm-modify-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Send Request</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showModifyApprove} onOpenChange={setShowModifyApprove}>
        <DialogContent className="max-w-2xl" data-testid="modify-approve-modal">
          <DialogHeader><DialogTitle>Modify & Approve</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">Adjust the order and approve it in one step — the requester gets a summary of exactly what changed.</p>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {modItems.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[13px]" data-testid={`mod-line-${idx}`}>
                  <span className="flex-1 truncate">{it.description}</span>
                  <input type="number" min="0" step="any" value={it.qty} onChange={(e) => setModItems(modItems.map((m, i) => i === idx ? { ...m, qty: e.target.value } : m))} data-testid={`mod-qty-${idx}`} className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-right" />
                  <span className="w-24 text-right mono text-slate-500">{it.unit_price === null || it.unit_price === "" ? "TBD" : `$${Number(it.unit_price).toFixed(2)}`}</span>
                  <button onClick={() => setModItems(modItems.filter((_, i) => i !== idx))} data-testid={`mod-remove-${idx}`} className="text-slate-300 hover:text-red-500 text-[12px] font-semibold">Remove</button>
                </div>
              ))}
              {modItems.length === 0 && <div className="text-[12.5px] text-red-500">At least one line is required — reject instead of emptying the order.</div>}
            </div>
            <textarea value={modComment} onChange={(e) => setModComment(e.target.value)} placeholder="Note to the requester (optional)" rows={2} data-testid="modify-approve-comment-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitModifyApprove} disabled={modItems.length === 0} data-testid="confirm-modify-approve-button" className="w-full h-10 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold disabled:opacity-50">Approve with these changes</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOverride} onOpenChange={setShowOverride}>
        <DialogContent data-testid="override-modal">
          <DialogHeader><DialogTitle>Override Approval</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500 flex items-start gap-1.5">
              <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
              This force-approves the request and skips any remaining approval steps. A reason (at least 10 characters) is required and will be recorded in the audit trail.
            </p>
            <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason for overriding approval *" rows={3} data-testid="override-reason-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitOverride} data-testid="confirm-override-button" className="w-full h-10 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold">Override &amp; Approve</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
